'use client'

import { useState, useEffect } from 'react'
import { CheckCircle, AlertCircle, Info, X, XCircle } from 'lucide-react'
import { cn } from '@/utils/cn'
import { useTheme } from '@/contexts/ThemeContext'

export interface Toast {
  id: string
  type: 'success' | 'error' | 'info' | 'warning'
  title: string
  description?: string
  duration?: number
}

interface ToastProps extends Toast {
  onRemove: (id: string) => void
}

const toastIcons = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertCircle,
}

const getToastStyles = (type: string, theme: string) => {
  const lightStyles = {
    success: 'bg-green-50 border-green-200 text-green-800 shadow-lg',
    error: 'bg-red-50 border-red-200 text-red-800 shadow-lg',
    info: 'bg-blue-50 border-blue-200 text-blue-800 shadow-lg',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800 shadow-lg',
  }
  
  const darkStyles = {
    success: 'bg-green-900/30 border-green-500/50 text-green-200',
    error: 'bg-red-900/30 border-red-500/50 text-red-200',
    info: 'bg-blue-900/30 border-blue-500/50 text-blue-200',
    warning: 'bg-yellow-900/30 border-yellow-500/50 text-yellow-200',
  }
  
  return theme === 'light' ? lightStyles[type] : darkStyles[type]
}

const iconStyles = {
  success: 'text-green-500 dark:text-green-400',
  error: 'text-red-500 dark:text-red-400',
  info: 'text-blue-500 dark:text-blue-400',
  warning: 'text-yellow-500 dark:text-yellow-400',
}

function ToastComponent({ id, type, title, description, duration = 5000, onRemove }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true)
  const [isExiting, setIsExiting] = useState(false)
  const { theme } = useTheme()
  
  const Icon = toastIcons[type]

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true)
      setTimeout(() => {
        setIsVisible(false)
        onRemove(id)
      }, 300) // Wait for exit animation
    }, duration)

    return () => clearTimeout(timer)
  }, [id, duration, onRemove])

  const handleClose = () => {
    setIsExiting(true)
    setTimeout(() => {
      setIsVisible(false)
      onRemove(id)
    }, 300)
  }

  if (!isVisible) return null

  return (
    <div
      className={cn(
        'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg border shadow-lg transition-all duration-300 ease-out',
        getToastStyles(type, theme),
        isExiting 
          ? 'translate-x-full opacity-0 scale-95' 
          : 'translate-x-0 opacity-100 scale-100'
      )}
      style={{ minWidth: '320px', maxWidth: '400px' }}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Icon className={cn('h-5 w-5', iconStyles[type])} />
          </div>
          <div className="ml-3 w-0 flex-1">
            <p className="text-sm font-medium">{title}</p>
            {description && (
              <p className="mt-1 text-sm opacity-75">{description}</p>
            )}
          </div>
          <div className="ml-4 flex flex-shrink-0">
            <button
              type="button"
              className="inline-flex rounded-md opacity-75 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-opacity"
              onClick={handleClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="h-1 w-full bg-black/10 dark:bg-white/10">
        <div 
          className={cn(
            'h-full transition-all ease-linear',
            type === 'success' ? 'bg-green-500 dark:bg-green-400' :
            type === 'error' ? 'bg-red-500 dark:bg-red-400' :
            type === 'info' ? 'bg-blue-500 dark:bg-blue-400' :
            'bg-yellow-500 dark:bg-yellow-400'
          )}
          style={{
            width: '100%',
            animation: `shrink ${duration}ms linear forwards`,
          }}
        />
      </div>

      <style jsx>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  )
}

export default ToastComponent